/** The networks that can be linked in the foot of an email, by id; the value is the network's own name (a proper noun, the same in every language). */
export const SOCIAL_NETWORKS = {
  facebook: "Facebook",
  instagram: "Instagram",
  x: "X",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  tiktok: "TikTok",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  pinterest: "Pinterest",
  github: "GitHub",
} as const;

export type SocialNetwork = keyof typeof SOCIAL_NETWORKS;

/** One link to a social network page: the network and its https address. */
export type SocialLink = { network: SocialNetwork; url: string };

export type SocialLinksResult = { ok: true; links: SocialLink[] } | { ok: false; code: "social-links-invalid" };

const MAX_LINKS = 12;
const MAX_URL_LENGTH = 300;
const INVALID = { ok: false, code: "social-links-invalid" } as const;

function secureUrl(value: string): boolean {
  if (value.length > MAX_URL_LENGTH || !URL.canParse(value)) return false;
  const url = new URL(value);
  return url.protocol === "https:" && url.username === "" && url.password === "" && url.hostname.includes(".");
}

/** Checks the saved text of the social links: empty (none), or one link per line written `network address` with a known network, each network once, at most 12 lines, every address an https web address without a login in it. Gives the links, or the code to show. */
export function verifySocialLinks(raw: string): SocialLinksResult {
  if (raw.trim() === "") return { ok: true, links: [] };
  const lines = raw.split("\n");
  if (lines.length > MAX_LINKS) return INVALID;
  const links: SocialLink[] = [];
  for (const line of lines) {
    const [network, url, ...extra] = line.split(" ");
    if (extra.length > 0 || !url || !(network in SOCIAL_NETWORKS) || !secureUrl(url)) return INVALID;
    if (links.some((l) => l.network === network)) return INVALID;
    links.push({ network: network as SocialNetwork, url });
  }
  return { ok: true, links };
}
